import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
  fetchRentalDetail,
  updateRental,
  fetchAdminRentalDetail,
  fetchAdminContractSummary
} from '../../Redux/Actions/actions';
import {
  FaHome,
  FaUser,
  FaDollarSign,
  FaCalendarAlt,
  FaFileContract,
  FaEdit,
  FaArrowLeft,
  FaPrint,
  FaDownload,
  FaTimesCircle
} from 'react-icons/fa';
import CancelRentalModal from '../../Components/CancelRentalModal/CancelRentalModal';
import {
  RENTAL_TYPE,
  useRentalType,
  getDetailPath,
  formatCurrency,
  formatDate,
  typeLabel
} from '../RentalManagement/utils/rentalTypeHelpers';
import style from './RentalDetail.module.css';

// Normaliza el detalle según el tipo.
// Regular: rentalDetail viene como { rental: {...}, ...propertyFields } o anidado.
// Admin: adminRentalDetail viene flat con campos embebidos.
const normalizeDetail = (raw, type) => {
  if (!raw) return null;
  if (type === RENTAL_TYPE.ADMIN) {
    return {
      id: raw.id,
      type: RENTAL_TYPE.ADMIN,
      contract: {
        id: raw.id,
        contractStart: raw.contractStart || raw.startDate,
        contractEnd: raw.contractEnd || raw.endDate,
        currentRentValue: raw.currentRentValue,
        rentValue: raw.rentValue,
        currency: raw.currency,
        deposit: raw.depositAmount,
        depositCurrency: raw.depositCurrency,
        paymentDueDay: raw.paymentDueDay,
        adjustmentType: raw.adjustmentType,
        adjustmentFrequency: raw.adjustmentFrequency,
        adjustmentPercentage: raw.adjustmentPercentage,
        contractStatus: raw.contractStatus,
        isActive: raw.isActive,
        adminFeePercent: raw.adminFeePercent,
        notes: raw.notes,
        specialClauses: raw.specialClauses,
        terminationDate: raw.terminationDate,
        terminationReason: raw.terminationReason
      },
      property: {
        address:
          raw.propertyAddress ||
          (raw.property
            ? `${raw.property.street || ''} ${raw.property.number || ''}, ${
                raw.property.locality || ''
              }`.trim()
            : ''),
        type:
          raw.property?.propertyType ||
          raw.propertyType ||
          'No especificado',
        environments: raw.property?.environments ?? raw.environments ?? '—',
        rooms: raw.property?.rooms ?? raw.rooms ?? '—',
        bathrooms: raw.property?.bathrooms ?? raw.bathrooms ?? '—',
        state: raw.property?.propertyState || raw.propertyState || '—'
      },
      owner: {
        name: raw.ownerName,
        phone: raw.ownerPhone,
        email: raw.ownerEmail
      }
    };
  }

  // Regular: estructura anidada
  const root = raw.rental ? raw : { rental: raw };
  const rental = root.rental;
  return {
    id: rental?.id || raw.id,
    type: RENTAL_TYPE.REGULAR,
    contract: {
      id: rental?.id,
      contractStart: rental?.contractStart,
      contractEnd: rental?.contractEnd,
      currentRentValue: rental?.currentRentValue,
      rentValue: rental?.rentValue,
      currency: rental?.currency,
      deposit: rental?.deposit ?? rental?.depositAmount,
      depositCurrency: rental?.depositCurrency,
      paymentDueDay: rental?.paymentDueDay,
      adjustmentType: rental?.adjustmentType,
      adjustmentFrequency: rental?.adjustmentFrequency,
      adjustmentPercentage: rental?.adjustmentPercentage,
      contractStatus: rental?.contractStatus,
      isActive: rental?.isActive,
      adminFeePercent: rental?.adminFeePercent,
      notes: rental?.notes,
      specialClauses: rental?.specialClauses,
      terminationDate: rental?.terminationDate,
      terminationReason: rental?.terminationReason
    },
    property: {
      address: `${raw.street || ''} ${raw.number || ''}, ${
        raw.locality || ''
      }, ${raw.province || ''}`.trim(),
      type: raw.propertyType || 'No especificado',
      environments: raw.environments ?? '—',
      rooms: raw.rooms ?? '—',
      bathrooms: raw.bathrooms ?? '—',
      state: raw.propertyState || '—'
    },
    owner: {
      name: raw.ownerName || rental?.ownerName,
      phone: raw.ownerPhone || rental?.ownerPhone,
      email: raw.ownerEmail || rental?.ownerEmail
    }
  };
};

const RentalDetail = () => {
  const { contractId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const type = useRentalType();
  const isAdmin = type === RENTAL_TYPE.ADMIN;

  const {
    rentalDetail,
    adminRentalDetail,
    rentalsLoading,
    adminRentalsLoading,
    rentalsError,
    adminRentalsError
  } = useSelector((state) => state);

  const loading = isAdmin ? adminRentalsLoading : rentalsLoading;
  const error = isAdmin ? adminRentalsError : rentalsError;
  const rawDetail = isAdmin ? adminRentalDetail : rentalDetail;

  const detail = useMemo(
    () => normalizeDetail(rawDetail, type),
    [rawDetail, type]
  );

  const [showEditFeeModal, setShowEditFeeModal] = useState(false);
  const [editFeeValue, setEditFeeValue] = useState('');
  const [editFeeError, setEditFeeError] = useState('');
  const [editFeeSaving, setEditFeeSaving] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);

  useEffect(() => {
    if (isAdmin) {
      dispatch(fetchAdminRentalDetail(contractId));
      dispatch(fetchAdminContractSummary(contractId)).catch(() => {});
    } else {
      dispatch(fetchRentalDetail(contractId));
    }
  }, [dispatch, contractId, isAdmin]);

  const openEditFeeModal = () => {
    setEditFeeValue(detail?.contract?.adminFeePercent ?? 10);
    setEditFeeError('');
    setShowEditFeeModal(true);
  };
  const closeEditFeeModal = () => {
    if (editFeeSaving) return;
    setShowEditFeeModal(false);
    setEditFeeError('');
  };
  const handleSaveAdminFee = async () => {
    setEditFeeError('');
    const num = Number(editFeeValue);
    if (
      editFeeValue === '' ||
      editFeeValue === null ||
      !Number.isFinite(num) ||
      !Number.isInteger(num) ||
      num < 0 ||
      num > 100
    ) {
      setEditFeeError(
        'Debe ser un número entero entre 0 y 100 (sin decimales)'
      );
      return;
    }
    try {
      setEditFeeSaving(true);
      // Solo regular soporta este endpoint vía updateRental
      await dispatch(updateRental(detail.contract.id, { adminFeePercent: num }));
      await dispatch(fetchRentalDetail(contractId));
      setShowEditFeeModal(false);
      toast.success('Canon de administración actualizado.');
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        'Error al actualizar el canon de administración';
      setEditFeeError(msg);
    } finally {
      setEditFeeSaving(false);
    }
  };

  const calculateContractDuration = () => {
    if (!detail?.contract?.contractStart || !detail?.contract?.contractEnd) {
      return 'No definido';
    }
    const start = new Date(detail.contract.contractStart);
    const end = new Date(detail.contract.contractEnd);
    const diffTime = end - start;
    const diffMonths = Math.round(diffTime / (1000 * 60 * 60 * 24 * 30));
    return `${diffMonths} meses`;
  };

  const getDaysUntilExpiry = () => {
    if (!detail?.contract?.contractEnd) return null;
    const today = new Date();
    const expiryDate = new Date(detail.contract.contractEnd);
    const diffTime = expiryDate - today;
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  if (loading && !detail) {
    return (
      <div className={style.container}>
        <div className={style.loading}>
          <p>Cargando detalles del contrato...</p>
        </div>
      </div>
    );
  }
  if (error && !detail) {
    return (
      <div className={style.container}>
        <div className={style.notFound}>
          <h2>Error al cargar el contrato</h2>
          <p>{error}</p>
          <Link to="/alquileres" className={style.backButton}>
            <FaArrowLeft /> Volver a Alquileres
          </Link>
        </div>
      </div>
    );
  }
  if (!detail) {
    return (
      <div className={style.container}>
        <div className={style.notFound}>
          <h2>Contrato no encontrado</h2>
          <p>No se pudo encontrar el contrato con ID: {contractId}</p>
          <Link to="/alquileres" className={style.backButton}>
            <FaArrowLeft /> Volver a Alquileres
          </Link>
        </div>
      </div>
    );
  }

  const daysLeft = getDaysUntilExpiry();
  const isExpiring = daysLeft !== null && daysLeft <= 90 && daysLeft > 0;
  const canCancel =
    detail.contract.contractStatus === 'active' ||
    detail.contract.contractStatus === 'pending';

  return (
    <div className={style.container}>
      <div className={style.header}>
        <div className={style.headerLeft}>
          <button
            onClick={() => navigate('/alquileres')}
            className={style.backBtn}
          >
            <FaArrowLeft /> Volver
          </button>
          <div>
            <h1 className={style.title}>
              <span
                style={{
                  display: 'inline-block',
                  padding: '2px 10px',
                  borderRadius: 999,
                  fontSize: '0.7rem',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  background: isAdmin ? '#fef3c7' : '#dbeafe',
                  color: isAdmin ? '#92400e' : '#1e40af',
                  marginRight: 10,
                  verticalAlign: 'middle'
                }}
                title={typeLabel(type)}
              >
                {isAdmin ? 'Solo Admin' : 'Regular'}
              </span>
              Detalles del Contrato
            </h1>
            <p className={style.subtitle}>Contrato ID: {detail.id}</p>
          </div>
        </div>

        <div className={style.headerActions}>
          <button className={style.actionBtn}>
            <FaPrint /> Imprimir
          </button>
          <button className={style.actionBtn}>
            <FaDownload /> Descargar PDF
          </button>
          {!isAdmin && (
            <button className={style.actionBtn} onClick={openEditFeeModal}>
              <FaEdit /> Editar
            </button>
          )}
          {canCancel && (
            <button
              className={style.actionBtn}
              style={{ background: '#dc3545', color: '#fff' }}
              onClick={() => setShowCancelModal(true)}
            >
              <FaTimesCircle /> Cancelar
            </button>
          )}
        </div>
      </div>

      {isExpiring && (
        <div className={style.alert}>
          <FaCalendarAlt />
          <span>
            ⚠️ Este contrato vence en {daysLeft} días (
            {formatDate(detail.contract.contractEnd)})
          </span>
        </div>
      )}

      <div className={style.content}>
        <div className={style.section}>
          <h2 className={style.sectionTitle}>
            <FaHome /> Información de la Propiedad
          </h2>
          <div className={style.grid}>
            <div className={style.infoGroup}>
              <label>Dirección</label>
              <p>{detail.property.address || '—'}</p>
            </div>
            <div className={style.infoGroup}>
              <label>Tipo de Propiedad</label>
              <p>{detail.property.type}</p>
            </div>
            <div className={style.infoGroup}>
              <label>Ambientes</label>
              <p>{detail.property.environments}</p>
            </div>
            <div className={style.infoGroup}>
              <label>Dormitorios</label>
              <p>{detail.property.rooms}</p>
            </div>
            <div className={style.infoGroup}>
              <label>Baños</label>
              <p>{detail.property.bathrooms}</p>
            </div>
            <div className={style.infoGroup}>
              <label>Estado</label>
              <p>{detail.property.state}</p>
            </div>
          </div>
        </div>

        <div className={style.section}>
          <h2 className={style.sectionTitle}>
            <FaUser /> Propietario
          </h2>
          <div className={style.grid}>
            <div className={style.infoGroup}>
              <label>Nombre</label>
              <p>{detail.owner.name || 'Sin asignar'}</p>
            </div>
            <div className={style.infoGroup}>
              <label>Teléfono</label>
              <p>{detail.owner.phone || 'No disponible'}</p>
            </div>
            <div className={style.infoGroup}>
              <label>Email</label>
              <p>{detail.owner.email || 'No disponible'}</p>
            </div>
          </div>
        </div>

        <div className={style.section}>
          <h2 className={style.sectionTitle}>
            <FaFileContract /> Detalles del Contrato
          </h2>
          <div className={style.grid}>
            <div className={style.infoGroup}>
              <label>Fecha de Inicio</label>
              <p>
                {detail.contract.contractStart
                  ? formatDate(detail.contract.contractStart)
                  : 'No definido'}
              </p>
            </div>
            <div className={style.infoGroup}>
              <label>Fecha de Finalización</label>
              <p>
                {detail.contract.contractEnd
                  ? formatDate(detail.contract.contractEnd)
                  : 'No definido'}
              </p>
            </div>
            <div className={style.infoGroup}>
              <label>Duración</label>
              <p>{calculateContractDuration()}</p>
            </div>
            <div className={style.infoGroup}>
              <label>Día de Pago</label>
              <p>Día {detail.contract.paymentDueDay || 10} de cada mes</p>
            </div>
            <div className={style.infoGroup}>
              <label>Tipo de Ajuste</label>
              <p>{(detail.contract.adjustmentType || 'ICL').toUpperCase()}</p>
            </div>
            <div className={style.infoGroup}>
              <label>Frecuencia de Ajuste</label>
              <p>Cada {detail.contract.adjustmentFrequency || 12} meses</p>
            </div>
          </div>
        </div>

        <div className={style.section}>
          <h2 className={style.sectionTitle}>
            <FaDollarSign /> Información Financiera
          </h2>
          <div className={style.grid}>
            <div className={style.infoGroup}>
              <label>Valor Inicial del Alquiler</label>
              <p className={style.price}>
                {formatCurrency(detail.contract.rentValue)}
                <span className={style.currency}>
                  {' '}
                  {detail.contract.currency || 'ARS'}
                </span>
              </p>
            </div>
            <div className={style.infoGroup}>
              <label>Valor Actual del Alquiler</label>
              <p className={style.price}>
                {formatCurrency(
                  detail.contract.currentRentValue ||
                    detail.contract.rentValue
                )}
                <span className={style.currency}>
                  {' '}
                  {detail.contract.currency || 'ARS'}
                </span>
              </p>
            </div>
            <div className={style.infoGroup}>
              <label>Depósito</label>
              <p>{formatCurrency(detail.contract.deposit)}</p>
            </div>
            <div className={style.infoGroup}>
              <label>Canon de Administración</label>
              <p>
                {detail.contract.adminFeePercent ?? 10}%
                {!isAdmin && (
                  <button
                    type="button"
                    onClick={openEditFeeModal}
                    className={style.actionBtn}
                    style={{
                      marginLeft: '8px',
                      padding: '4px 8px',
                      fontSize: '0.85em'
                    }}
                    title="Editar canon de administración"
                  >
                    <FaEdit />
                  </button>
                )}
              </p>
            </div>
          </div>
        </div>

        <div className={style.section}>
          <h2 className={style.sectionTitle}>Acciones</h2>
          <div className={style.quickActions}>
            <Link
              to={getDetailPath(detail.id, type, 'pagos')}
              className={style.quickActionBtn}
            >
              <FaDollarSign />
              <span>Ver Pagos</span>
            </Link>
            <Link
              to={getDetailPath(detail.id, type, 'inquilinos')}
              className={style.quickActionBtn}
            >
              <FaUser />
              <span>Gestionar Inquilinos</span>
            </Link>
            <Link
              to={getDetailPath(detail.id, type, 'ajustes')}
              className={style.quickActionBtn}
            >
              <FaFileContract />
              <span>Ajustes del Contrato</span>
            </Link>
          </div>
        </div>
      </div>

      {showEditFeeModal && (
        <div
          onClick={closeEditFeeModal}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#fff',
              borderRadius: 8,
              padding: 24,
              minWidth: 320,
              maxWidth: 420,
              boxShadow: '0 10px 30px rgba(0,0,0,0.2)'
            }}
          >
            <h3 style={{ marginTop: 0 }}>Editar canon de administración</h3>
            <p style={{ fontSize: '0.9em', color: '#555' }}>
              Porcentaje aplicado sobre cada cuota. Entero entre 0 y 100.
            </p>
            <label
              style={{ display: 'block', marginBottom: 8, fontWeight: 600 }}
            >
              Porcentaje (%)
            </label>
            <input
              type="number"
              min="0"
              max="100"
              step="1"
              value={editFeeValue}
              onChange={(e) => setEditFeeValue(e.target.value)}
              onKeyDown={(e) => {
                if (
                  e.key === '.' ||
                  e.key === ',' ||
                  e.key === 'e' ||
                  e.key === '+' ||
                  e.key === '-'
                ) {
                  e.preventDefault();
                }
              }}
              disabled={editFeeSaving}
              style={{
                width: '100%',
                padding: 8,
                border: '1px solid #ccc',
                borderRadius: 4,
                fontSize: '1em'
              }}
            />
            {editFeeError && (
              <div
                style={{
                  color: '#c62828',
                  marginTop: 8,
                  fontSize: '0.9em'
                }}
              >
                {editFeeError}
              </div>
            )}
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 8,
                marginTop: 16
              }}
            >
              <button
                type="button"
                onClick={closeEditFeeModal}
                disabled={editFeeSaving}
                className={style.actionBtn}
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleSaveAdminFee}
                disabled={editFeeSaving}
                className={style.actionBtn}
                style={{ background: '#2563eb', color: '#fff' }}
              >
                {editFeeSaving ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showCancelModal && (
        <CancelRentalModal
          rentalId={detail.contract.id}
          propertyTitle={detail.property.address}
          type={type}
          onClose={() => setShowCancelModal(false)}
          onSuccess={() => {
            setShowCancelModal(false);
            if (isAdmin) {
              dispatch(fetchAdminRentalDetail(contractId));
            } else {
              dispatch(fetchRentalDetail(contractId));
            }
          }}
        />
      )}
    </div>
  );
};

export default RentalDetail;
